import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/core/store/hooks';
import {
  loginStart,
  loginSuccess,
  logout as logoutAction
} from '../store/authSlice';
import { 
  login, 
  register, 
  logout, 
  requestPasswordReset, 
  confirmPasswordReset 
} from '../api/authApi';
import type { 
  LoginRequest, 
  RegisterRequest, 
  PasswordResetRequestParams, 
  PasswordResetConfirmParams 
} from '../types/authTypes';
import { notifySuccess } from '@/core/utils/toastUtils';

export function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const refreshToken = useAppSelector(state => state.auth.refreshToken);

  async function handleLogin(credentials: LoginRequest) {
    try {
      setIsSubmitting(true);
      dispatch(loginStart());
      const response = await login(credentials);      
      dispatch(loginSuccess(response));
      notifySuccess('Successfully logged in');
      navigate('/management/devices');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRegister(userData: RegisterRequest) {
    try {
      setIsSubmitting(true);
      dispatch(loginStart());
      const response = await register(userData);
      notifySuccess(response?.message || 'Registration successful! Please check your email for verification link.');
      navigate('/login');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLogout() {
    try {
      setIsSubmitting(true);
      if (refreshToken) {
        await logout({ refresh: refreshToken });
      }
      dispatch(logoutAction());
      notifySuccess('Successfully logged out');
      navigate('/login');
    } catch (error) {
      // Error notifications are already handled by apiClient
      dispatch(logoutAction());
      navigate('/login');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordResetRequest(params: PasswordResetRequestParams) {
    try {
      setIsSubmitting(true);
      await requestPasswordReset(params);
      notifySuccess('Password reset instructions have been sent to your email');
      return true;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordResetConfirm(params: PasswordResetConfirmParams) {
    try {
      setIsSubmitting(true);
      await confirmPasswordReset(params);
      notifySuccess('Your password has been reset successfully');
      navigate('/login');
      return true;
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    requestPasswordReset: handlePasswordResetRequest,
    confirmPasswordReset: handlePasswordResetConfirm,
    isSubmitting
  };
}